import React, { useState, useEffect } from 'react';
import { Package, ShoppingCart, Tags, Users, Truck, BarChart3, DollarSign, MessageCircle, Settings, LayoutDashboard, Facebook, ChevronRight, Megaphone, HardDrive, MessageSquare, Bot, Wand2, Calculator, KeyRound, Copy, RefreshCw, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Permanent API key + secret box — credentials the owner can hand to 3rd parties
const ApiCredsBox = () => {
  const [creds, setCreds] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/api-credentials`).then(r => setCreds(r.data)).catch(() => {});
  }, []);
  const copy = (label, val) => {
    navigator.clipboard.writeText(val);
    toast.success(`${label} הועתק ✓`, { duration: 1500 });
  };
  const regenerate = async () => {
    if (!window.confirm('ליצור מפתחות חדשים? הישנים יפסיקו להיות תקפים אצל צד ג\'.')) return;
    try {
      const r = await axios.post(`${API}/admin/api-credentials/regenerate`);
      setCreds(r.data);
      toast.success('מפתחות חדשים נוצרו ✓');
    } catch (_e) { toast.error('שגיאה'); }
  };
  if (!creds) return null;
  return (
    <div className="p-3 border-t border-white/10 space-y-1.5" data-testid="sidebar-api-creds">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-white/30 font-bold flex items-center gap-1">
          <KeyRound className="w-3 h-3" /> API לצד שלישי
        </p>
        <button onClick={regenerate} className="p-1 hover:bg-white/10 rounded" title="חדש מפתחות" data-testid="api-creds-regenerate">
          <RefreshCw className="w-3 h-3 text-white/40" />
        </button>
      </div>
      <button
        onClick={() => copy('API Key', creds.api_key)}
        className="w-full flex items-center gap-1.5 bg-white/5 hover:bg-white/10 rounded px-2 py-1.5 text-right"
        data-testid="api-creds-copy-key"
      >
        <Copy className="w-3 h-3 text-white/40 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] text-white/40">API Key</p>
          <p className="text-[10px] text-emerald-300 truncate" dir="ltr">{creds.api_key}</p>
        </div>
      </button>
      <button
        onClick={() => copy('Secret', creds.api_secret)}
        className="w-full flex items-center gap-1.5 bg-white/5 hover:bg-white/10 rounded px-2 py-1.5 text-right"
        data-testid="api-creds-copy-secret"
      >
        <Copy className="w-3 h-3 text-white/40 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] text-white/40">Secret</p>
          <p className="text-[10px] text-amber-300 truncate" dir="ltr">{creds.api_secret}</p>
        </div>
      </button>
    </div>
  );
};

const MENU_ITEMS = [
  { id: 'dashboard', label: 'דשבורד', icon: LayoutDashboard },
  { id: 'orders', label: 'הזמנות', icon: ShoppingCart },
  { id: 'assistant', label: 'עוזר אישי', icon: Bot },
  { id: 'site_editor', label: 'צ\'אט שינויים', icon: Wand2 },
  { id: 'products', label: 'מוצרים', icon: Package },
  { id: 'coupons', label: 'קופונים', icon: Tags },
  { id: 'banner', label: 'באנר', icon: Megaphone },
  { id: 'customers', label: 'לקוחות', icon: Users },
  { id: 'customer_export', label: 'ייצוא לקוחות ל-Google Sheets', icon: FileSpreadsheet },
  { id: 'shipping', label: 'משלוחים', icon: Truck },
  { id: 'facebook', label: 'פייסבוק', icon: Facebook },
  { id: 'financial', label: 'פיננסי', icon: DollarSign },
  { id: 'wasender', label: 'וואטסאפ בוט', icon: MessageSquare },
  { id: 'whatsapp', label: 'וואטסאפ', icon: MessageCircle },
  { id: 'analytics', label: 'אנליטיקס', icon: BarChart3 },
  { id: 'backup', label: 'גיבוי', icon: HardDrive },
  { id: 'settings', label: 'הגדרות', icon: Settings },
];

// Separate "external workspaces" section — businesses/projects that are NOT
// part of בריאות הגבר. Clicking opens a fully separate page (own URL, login,
// PWA install). Each entry has `external: true` + `path` (full URL path).
const EXTERNAL_ITEMS = [
  { id: 'nasrallah', label: 'נסראללה אבתיסאם', subLabel: 'ראיית חשבון', icon: Calculator, path: '/nasrallah' },
];

const AdminSidebar = ({ activeSection, setActiveSection, collapsed, setCollapsed, orderCount }) => {
  return (
    <aside
      className={`fixed top-0 right-0 h-full bg-[#1a1a2e] text-white z-40 transition-all duration-300 flex flex-col ${collapsed ? 'w-16' : 'w-56'}`}
      dir="rtl"
      data-testid="admin-sidebar"
    >
      {/* Header */}
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#D97706] rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-sm">BG</span>
            </div>
            <div>
              <p className="text-sm font-bold leading-tight">בריאות הגבר</p>
              <p className="text-[10px] text-white/40">ניהול מערכת</p>
            </div>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`p-1.5 hover:bg-white/10 rounded-lg transition-colors ${collapsed ? 'mx-auto' : ''}`}
          data-testid="sidebar-toggle"
        >
          <ChevronRight className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Menu */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {MENU_ITEMS.map(item => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-all relative ${
                isActive
                  ? 'bg-[#D97706]/20 text-[#D97706] font-bold'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              } ${collapsed ? 'justify-center' : ''}`}
              data-testid={`sidebar-${item.id}`}
              title={collapsed ? item.label : ''}
            >
              {isActive && <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#D97706] rounded-l" />}
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
              {!collapsed && item.id === 'orders' && orderCount > 0 && (
                <span className="mr-auto bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {orderCount > 99 ? '99+' : orderCount}
                </span>
              )}
            </button>
          );
        })}

        {/* External workspaces — separate businesses/projects */}
        {!collapsed && (
          <div className="mt-4 pt-3 border-t border-white/10">
            <p className="px-3 pb-1 text-[10px] uppercase tracking-wider text-white/30 font-bold">עסקים נוספים</p>
          </div>
        )}
        {collapsed && <div className="my-3 mx-3 border-t border-white/10" />}
        {EXTERNAL_ITEMS.map(item => {
          const Icon = item.icon;
          return (
            <a
              key={item.id}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-all relative text-white/60 hover:text-white hover:bg-white/5 ${collapsed ? 'justify-center' : ''}`}
              data-testid={`sidebar-ext-${item.id}`}
              title={collapsed ? item.label : ''}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && (
                <div className="flex-1 leading-tight">
                  <p className="font-bold">{item.label}</p>
                  {item.subLabel && <p className="text-[10px] text-white/40">{item.subLabel}</p>}
                </div>
              )}
            </a>
          );
        })}
      </nav>

      {/* Footer */}
      {!collapsed && <ApiCredsBox />}
      {!collapsed && (
        <div className="p-3 border-t border-white/10">
          <p className="text-[10px] text-white/30 text-center">menshealth.co.il</p>
        </div>
      )}
    </aside>
  );
};

export default AdminSidebar;
export { MENU_ITEMS };
